export function canPlayMotion({ visible, reducedMotion, documentHidden }) {
  return visible && !reducedMotion && !documentHidden;
}

export class LSMotionGrid extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.videos = [...this.querySelectorAll('[data-ls-motion-video]')];
    this.visibility = new Map(this.videos.map((video) => [video, false]));
    this.motionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.isMotionGridConnected = true;

    const { signal } = this.abortController;
    document.addEventListener('visibilitychange', () => this.syncAll(), { signal });
    this.motionQuery?.addEventListener('change', () => this.syncAll(), { signal });

    if (typeof globalThis.IntersectionObserver === 'function') {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.visibility.set(entry.target, entry.isIntersecting);
          this.syncVideo(entry.target);
        });
      }, { rootMargin: '200px 0px', threshold: 0.01 });
      this.videos.forEach((video) => this.observer.observe(video));
    } else {
      this.videos.forEach((video) => this.visibility.set(video, true));
    }

    this.syncAll();
  }

  disconnectedCallback() {
    this.isMotionGridConnected = false;
    this.abortController?.abort();
    this.abortController = null;
    this.observer?.disconnect();
    this.observer = null;
    this.videos?.forEach((video) => {
      video.pause();
      this.setVideoPlaying(video, false);
    });
  }

  stateFor(video) {
    return {
      visible: this.visibility.get(video) === true,
      reducedMotion: this.motionQuery?.matches || false,
      documentHidden: document.hidden,
    };
  }

  syncAll() {
    this.videos?.forEach((video) => this.syncVideo(video));
  }

  syncVideo(video) {
    const allowed = this.isMotionGridConnected && canPlayMotion(this.stateFor(video));
    if (!allowed) {
      video.pause();
      this.setVideoPlaying(video, false);
      return;
    }

    video.play().then(() => {
      if (this.isMotionGridConnected && canPlayMotion(this.stateFor(video))) {
        this.setVideoPlaying(video, true);
      } else {
        video.pause();
        this.setVideoPlaying(video, false);
      }
    }).catch(() => {
      this.setVideoPlaying(video, false);
    });
  }

  setVideoPlaying(video, playing) {
    video.closest('[data-ls-motion-tile]')?.classList.toggle('is-motion-playing', playing);
  }
}

if (!customElements.get('ls-motion-grid')) {
  customElements.define('ls-motion-grid', LSMotionGrid);
}
